/**
 * Intake Metrics Component
 * 
 * Displays summary metric cards for the intake dashboard
 */

import React from 'react';
import { useClinic } from '@/contexts/ClinicContext';
import { 
  FileText, 
  CheckCircle2, 
  Clock, 
  TrendingUp,
  Users,
  Timer,
  XCircle,
  Loader2,
} from 'lucide-react';

interface MetricsData {
  total: number;
  completed: number;
  pending: number;
  started: number;
  expired: number;
  cancelled: number;
  completionRate: number;
  avgCompletionMinutes: number;
  byCategory: Record<string, { total: number; completed: number }>;
  period: string;
}

interface IntakeMetricsProps {
  metrics: MetricsData | null;
  isLoading?: boolean;
}

interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ElementType;
  color: string;
  bgColor: string;
  trend?: number;
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  subtitle,
  icon: Icon,
  color,
  bgColor,
  trend,
}) => (
  <div className="rounded-xl bg-white border border-slate-200 p-3 hover:shadow-sm transition-shadow">
    <div className="flex items-start justify-between">
      <div className={`w-8 h-8 rounded-md flex items-center justify-center ${bgColor}`}>
        <Icon className={`w-4 h-4 ${color}`} />
      </div>
      {trend !== undefined && (
        <div className={`flex items-center text-[10px] ${trend >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
          <TrendingUp className={`w-3 h-3 mr-0.5 ${trend < 0 ? 'rotate-180' : ''}`} />
          {Math.abs(trend)}%
        </div>
      )}
    </div>
    <div className="mt-3">
      <p className="text-xl font-bold text-slate-900">{value}</p>
      <p className="text-xs text-slate-600 mt-0.5">{title}</p>
      {subtitle && (
        <p className="text-[11px] text-slate-400 mt-0.5">{subtitle}</p>
      )}
    </div>
  </div>
);

const MetricCardSkeleton: React.FC = () => (
  <div className="rounded-xl bg-white border border-slate-200 p-3 animate-pulse">
    <div className="w-8 h-8 rounded-md bg-slate-100" />
    <div className="mt-3">
      <div className="h-7 w-14 bg-slate-100 rounded" />
      <div className="h-3 w-20 bg-slate-100 rounded mt-1" />
    </div>
  </div>
);

export const IntakeMetrics: React.FC<IntakeMetricsProps> = ({ metrics, isLoading }) => {
  const { clinic } = useClinic();
  const formLabelPlural = clinic?.clinic_archetype === 'guided' ? 'Enrollments' : 'Intakes';
  if (isLoading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <MetricCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (!metrics) {
    return null;
  }

  const formatTime = (minutes: number): string => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const formatPeriod = (period: string): string => {
    // period is "ISO_DATE to ISO_DATE" — format both to readable dates
    const parts = period.split(' to ');
    if (parts.length !== 2) return period;
    const fmt = (iso: string) => {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return iso;
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };
    return `${fmt(parts[0])} to ${fmt(parts[1])}`;
  };

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <MetricCard
        title={`Total ${formLabelPlural}`}
        value={metrics.total}
        subtitle={formatPeriod(metrics.period)}
        icon={FileText}
        color="text-brand-teal"
        bgColor="bg-brand-teal/10"
      />
      <MetricCard
        title="Completed"
        value={metrics.completed}
        subtitle={`${metrics.completionRate}% completion rate`}
        icon={CheckCircle2}
        color="text-emerald-600"
        bgColor="bg-emerald-50"
      />
      <MetricCard
        title="In Progress"
        value={metrics.pending + metrics.started}
        subtitle={`${metrics.pending} pending, ${metrics.started} started`}
        icon={Clock}
        color="text-amber-600"
        bgColor="bg-amber-50"
      />
      <MetricCard
        title="Avg. Completion Time"
        value={formatTime(metrics.avgCompletionMinutes)}
        subtitle="From start to submit"
        icon={Timer}
        color="text-blue-600"
        bgColor="bg-blue-50"
      />
    </div>
  );
};

export default IntakeMetrics;
